import React from "react";
import { TFunction } from "i18next";
import { Dispatch } from "@reduxjs/toolkit";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "../../utils/formatUtils";
import {
  getInternalPlatformDisplayName,
  InternalPlatform,
} from "../../utils/internalPlatformUtils";

/**
 * Piezas compartidas por los dashboards de cliente de paid media (Google Ads y
 * social). Las filas guardan volúmenes y las tasas se derivan al pintar, para no
 * promediar tasas.
 */

type CampaignMetricsDTO = components["schemas"]["CampaignMetricsDTO"];

export interface PerformanceRow {
  key: string;
  label: string;
  type?: string;
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number;
  engagements: number;
  videoViews: number;
}

export const ratio = (numerator: number, denominator: number) =>
  denominator > 0 ? numerator / denominator : 0;

export const costPer = (cost: number, units: number) =>
  units > 0 ? cost / units : null;

const emptyRow = (
  key: string,
  label: string,
  type?: string
): PerformanceRow => ({
  key,
  label,
  type,
  cost: 0,
  impressions: 0,
  clicks: 0,
  conversions: 0,
  engagements: 0,
  videoViews: 0,
});

const addMetric = (row: PerformanceRow, metric: CampaignMetricsDTO) => {
  row.cost += metric.cost ?? 0;
  row.impressions += metric.impressions ?? 0;
  row.clicks += metric.clicks ?? 0;
  row.conversions += metric.conversions ?? 0;
  row.engagements += metric.engagements ?? 0;
  row.videoViews += metric.videoViews ?? 0;
};

/** Una fila por subcampaña (campaña, ad set o ad group enlazado), de más a menos inversión. */
export const rowsByCampaign = (
  rawMetrics: CampaignMetricsDTO[]
): PerformanceRow[] => {
  const byCampaign = new Map<string, PerformanceRow>();
  for (const metric of rawMetrics) {
    const row =
      byCampaign.get(metric.internalCampaignId) ??
      emptyRow(
        metric.internalCampaignId,
        metric.internalCampaignName,
        getInternalPlatformDisplayName(metric.platform as InternalPlatform)
      );
    addMetric(row, metric);
    byCampaign.set(metric.internalCampaignId, row);
  }
  return [...byCampaign.values()].sort((a, b) => b.cost - a.cost);
};

/** Una fila por día, del más reciente al más antiguo. */
export const rowsByDay = (
  rawMetrics: CampaignMetricsDTO[],
  formatDay: (date: string) => string
): PerformanceRow[] => {
  const byDay = new Map<string, PerformanceRow>();
  for (const metric of rawMetrics) {
    const row =
      byDay.get(metric.date) ?? emptyRow(metric.date, formatDay(metric.date));
    addMetric(row, metric);
    byDay.set(metric.date, row);
  }
  return [...byDay.values()].sort((a, b) => (a.key < b.key ? 1 : -1));
};

export const totalOf = (
  rows: PerformanceRow[],
  label: string
): PerformanceRow =>
  rows.reduce(
    (total, row) => {
      total.cost += row.cost;
      total.impressions += row.impressions;
      total.clicks += row.clicks;
      total.conversions += row.conversions;
      total.engagements += row.engagements;
      total.videoViews += row.videoViews;
      return total;
    },
    emptyRow("total", label)
  );

type DailyMetrics = NonNullable<
  components["schemas"]["MergedCampaignMetrics"]["metrics"]
>["dailyData"];

/** Exporta la serie diaria con el mismo formato que el resto de dashboards. */
export const exportDailyMetrics = (
  dailyData: DailyMetrics,
  fileName: string,
  t: TFunction,
  dispatch: Dispatch
) =>
  exportMetricsToCSV(
    dailyData.map((day) => ({
      date: day.date,
      impressions: day.impressions ?? 0,
      clicks: day.clicks ?? 0,
      ctr: day.ctr ?? 0,
      cpm: day.cpm ?? 0,
      cpc: day.cpc ?? 0,
      cost: day.cost ?? 0,
      cpv: day.cpv ?? 0,
      vtr: day.vtr ?? 0,
      dailyInvestment: day.cost ?? 0,
      dailyImpressions: day.impressions ?? 0,
      completeViews: day.completeViews ?? 0,
      dailyCTR: day.ctr ?? 0,
      conversions: day.conversions ?? 0,
    })),
    fileName,
    t,
    dispatch
  );

export const SectionTitle: React.FC<{ title: string; hint?: string }> = ({
  title,
  hint,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

export const cardGridSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 3,
  "& > *": { flex: "1 1 calc(25% - 24px)", minWidth: "180px" },
};

interface MetricsDateToolbarProps {
  startDate: string;
  endDate: string;
  campaignStartDate: string;
  campaignEndDate: string;
  onStartDateChange?: (value: string) => void;
  onEndDateChange?: (value: string) => void;
  onClear?: () => void;
  onExport: () => void;
  exportDisabled: boolean;
}

/** Cabecera con el filtro de fechas y la exportación de la serie diaria. */
export const MetricsDateToolbar: React.FC<MetricsDateToolbarProps> = ({
  startDate,
  endDate,
  campaignStartDate,
  campaignEndDate,
  onStartDateChange,
  onEndDateChange,
  onClear,
  onExport,
  exportDisabled,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {t("campaigns.metrics", { defaultValue: "Metrics" })}
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <TextField
          label={t("common.startDate", { defaultValue: "Start Date" })}
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => onStartDateChange?.(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: campaignStartDate || undefined,
            max: endDate || campaignEndDate || undefined,
          }}
          sx={{ width: 150 }}
        />
        <TextField
          label={t("common.endDate", { defaultValue: "End Date" })}
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => onEndDateChange?.(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: startDate || campaignStartDate || undefined,
            max: campaignEndDate || undefined,
          }}
          sx={{ width: 150 }}
          disabled={!startDate}
        />
        <IconButton
          onClick={onClear}
          size="small"
          color="primary"
          title={t("common.clearFilters", { defaultValue: "Clear Filters" })}
        >
          <ClearIcon />
        </IconButton>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={onExport}
          disabled={exportDisabled}
          sx={{ height: 36, textTransform: "none", fontWeight: 500 }}
        >
          {t("campaigns.exportMetrics", { defaultValue: "Export Data" })}
        </Button>
      </Box>
    </Box>
  );
};

interface PerformanceTableProps {
  rows: PerformanceRow[];
  firstColumn: string;
  currency?: string;
  showType?: boolean;
  showEngagements?: boolean;
  showVideoViews?: boolean;
  clicksLabel?: string;
}

/** Tabla de rendimiento con fila de total cuando hay más de una fila. */
export const PerformanceTable: React.FC<PerformanceTableProps> = ({
  rows,
  firstColumn,
  currency,
  showType = false,
  showEngagements = false,
  showVideoViews = false,
  clicksLabel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const notAvailable = t("common.notAvailable", { defaultValue: "N/A" });
  const money = (value: number | null) =>
    value === null ? notAvailable : formatCurrency(value, currency);
  const total = totalOf(
    rows,
    t("campaigns.googleAds.total", { defaultValue: "Total" })
  );
  const allRows = rows.length > 1 ? [...rows, total] : rows;

  const headers = [
    t("home.dashboardInvestment", { defaultValue: "Investment" }),
    t("campaigns.impressions", { defaultValue: "Impressions" }),
    clicksLabel ?? t("campaigns.clicks", { defaultValue: "Clicks" }),
    t("campaigns.ctr", { defaultValue: "CTR" }),
    t("campaigns.googleAds.avgCpc", { defaultValue: "Avg. CPC" }),
    ...(showEngagements
      ? [t("campaigns.social.engagements", { defaultValue: "Engagements" })]
      : []),
    ...(showVideoViews
      ? [t("campaigns.videoViews", { defaultValue: "Video views" })]
      : []),
    t("campaigns.totalConversions", { defaultValue: "Conversions" }),
    t("campaigns.conversionRate", { defaultValue: "Conversion Rate" }),
    t("campaigns.googleAds.costPerConversion", {
      defaultValue: "Cost / conv.",
    }),
  ];

  return (
    <TableContainer sx={{ maxHeight: 520 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{firstColumn}</TableCell>
            {showType && (
              <TableCell>
                {t("campaigns.googleAds.campaignType", {
                  defaultValue: "Type",
                })}
              </TableCell>
            )}
            {headers.map((header) => (
              <TableCell key={header} align="right">
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {allRows.map((row) => {
            const isTotal = row.key === "total";
            const cellSx = isTotal ? { fontWeight: 700 } : undefined;
            const cells = [
              formatCurrency(row.cost, currency),
              formatNumber(row.impressions),
              formatNumber(row.clicks),
              formatPercentage(ratio(row.clicks, row.impressions)),
              money(costPer(row.cost, row.clicks)),
              ...(showEngagements ? [formatNumber(row.engagements)] : []),
              ...(showVideoViews ? [formatNumber(row.videoViews)] : []),
              formatNumber(row.conversions),
              formatPercentage(ratio(row.conversions, row.clicks)),
              money(costPer(row.cost, row.conversions)),
            ];
            return (
              <TableRow
                key={row.key}
                sx={isTotal ? { bgcolor: theme.palette.grey[50] } : undefined}
              >
                <TableCell sx={cellSx}>{row.label}</TableCell>
                {showType && (
                  <TableCell sx={cellSx}>{row.type ?? ""}</TableCell>
                )}
                {cells.map((value, index) => (
                  <TableCell key={headers[index]} align="right" sx={cellSx}>
                    {value}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface VideoRetentionProps {
  videoViews: number;
  firstQuartile: number;
  midpoint: number;
  thirdQuartile: number;
  complete: number;
}

/** Retención del vídeo: qué parte de las reproducciones llega a cada cuartil. */
export const VideoRetention: React.FC<VideoRetentionProps> = ({
  videoViews,
  firstQuartile,
  midpoint,
  thirdQuartile,
  complete,
}) => {
  const { t } = useTranslation();
  const steps = [
    { label: "25%", value: firstQuartile },
    { label: "50%", value: midpoint },
    { label: "75%", value: thirdQuartile },
    { label: "100%", value: complete },
  ];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        {t("campaigns.social.retentionHint", {
          defaultValue:
            "Share of video plays that reach each point of the video",
        })}
      </Typography>
      {steps.map((step) => {
        const share = ratio(step.value, videoViews);
        return (
          <Box
            key={step.label}
            sx={{
              display: "grid",
              gridTemplateColumns: "48px 1fr 150px",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {step.label}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, share * 100)}
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Typography variant="body2" sx={{ textAlign: "right" }}>
              {`${formatNumber(step.value)} · ${formatPercentage(share)}`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
